import { useState, useCallback, useEffect } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useErrorMessage } from './errorMessage';

/**
 * Custom hook to load enums from an API endpoint.
 * This eliminates code duplication between different form field hooks.
 * @hook
 * @param {string} enumsUrl - The API endpoint URL to fetch enums from.
 * @param {Array} initialValue - The initial value for the enums state.
 * @returns {Object} Object containing enums data and loading state.
 * @returns {Object} returns.enums - The loaded enums data.
 * @returns {boolean} returns.isFieldsLoading - Loading state for the enums fetch.
 */
export const useEnumsLoader = (enumsUrl, initialValue = []) => {
  const [enums, setEnums] = useState(initialValue);
  const [isFieldsLoading, setIsFieldsLoading] = useState(true);
  const { message } = useErrorMessage();
  const { dispatchAPI } = useAuthContext();

  const getSelectOptions = useCallback(async () => {
    setIsFieldsLoading(true);
    try {
      const { data } = await dispatchAPI('GET', {
        url: enumsUrl
      });
      setEnums(data);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setIsFieldsLoading(false);
  }, [enumsUrl]);

  useEffect(() => {
    (async () => {
      await getSelectOptions();
    })();
  }, [getSelectOptions]);

  return { enums, isFieldsLoading };
};
